<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12" md="8">
          <v-card elevation="6" rounded="xxl" :loading="loading">
            <v-card-title class="headline primary--text">
              <v-icon class="mr-2">mdi-briefcase-edit</v-icon>
              Edit Work History
              <v-spacer></v-spacer>
              <v-btn
                color="error"
                small
                outlined
                @click="confirmDelete"
                :disabled="saving"
              >
                <v-icon left small>mdi-delete</v-icon>
                Delete
              </v-btn>
            </v-card-title>

            <v-card-text v-if="!loading">
              <!-- Employee Info Display -->
              <v-row v-if="workHistory && workHistory.user" class="mb-4">
                <v-col cols="12">
                  <v-card outlined class="pa-3">
                    <div class="d-flex align-center">
                      <v-avatar size="48" class="mr-3">
                        <v-img
                          v-if="workHistory.user.image_path"
                          :src="getImageUrl(workHistory.user.image_path)"
                          :alt="workHistory.user.first_name"
                        ></v-img>
                        <v-icon v-else color="grey lighten-1" size="24">mdi-account</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-h6 font-weight-medium">
                          {{ getUserName(workHistory.user) }}
                        </div>
                        <div class="text-body-2 grey--text">
                          {{ workHistory.user.email }}
                        </div>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <v-form ref="form" v-model="valid" lazy-validation>
                <v-row>
                  <!-- Employee Selection (Read-only in edit mode) -->
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.user_id"
                      :items="users"
                      item-text="name"
                      item-value="id"
                      label="Employee"
                      :rules="[rules.required]"
                      solo
                      rounded
                      dense
                      required
                      :loading="loadingUsers"
                      :disabled="true"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-account</v-icon>
                      </template>
                    </v-select>
                  </v-col>

                  <!-- Organization Name -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.organization_name"
                      label="Organization Name"
                      solo
                      rounded
                      dense
                      :rules="[rules.maxLength(255)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-domain</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Job Title -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.job_title"
                      label="Job Title"
                      solo
                      rounded
                      dense
                      :rules="[rules.maxLength(255)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-briefcase</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- City -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.city"
                      label="City"
                      solo
                      rounded
                      dense
                      :rules="[rules.maxLength(255)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-map-marker</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Start Date -->
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.start_date"
                          label="Start Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          solo
                          rounded
                          dense
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.start_date"
                        @input="startDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- End Date -->
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="endDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.end_date"
                          label="End Date (Leave empty for current position)"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          solo
                          rounded
                          dense
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          :rules="[rules.endDateAfterStart]"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.end_date"
                        @input="endDateMenu = false"
                        :min="form.start_date"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- Address -->
                  <v-col cols="12">
                    <v-textarea
                      v-model="form.address"
                      label="Address"
                      solo
                      rounded
                      dense
                      rows="3"
                      auto-grow
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-map</v-icon>
                      </template>
                    </v-textarea>
                  </v-col>

                  <!-- Description -->
                  <v-col cols="12">
                    <v-textarea
                      v-model="form.description"
                      label="Job Description & Responsibilities"
                      solo
                      rounded
                      dense
                      rows="4"
                      auto-grow
                      counter="1000"
                      :rules="[rules.maxLength(1000)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-text</v-icon>
                      </template>
                    </v-textarea>
                  </v-col>

                  <!-- Status -->
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="form.is_active"
                      label="Active Status"
                      color="success"
                      inset
                    ></v-switch>
                  </v-col>

                  <!-- Metadata -->
                  <v-col cols="12" v-if="workHistory">
                    <v-card outlined class="pa-3">
                      <v-row>
                        <v-col cols="12" md="6">
                          <div class="text-caption grey--text">Created At</div>
                          <div class="text-body-2">{{ formatDateTime(workHistory.created_at) }}</div>
                        </v-col>
                        <v-col cols="12" md="6">
                          <div class="text-caption grey--text">Updated At</div>
                          <div class="text-body-2">{{ formatDateTime(workHistory.updated_at) }}</div>
                        </v-col>
                        <v-col cols="12" v-if="workHistory.created_by">
                          <div class="text-caption grey--text">Created By</div>
                          <div class="text-body-2">{{ getUserName(workHistory.created_by) }}</div>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-6" v-if="!loading">
              <v-btn
                color="grey"
                text
                @click="goBack"
                :disabled="saving"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                @click="updateWorkHistory"
                :loading="saving"
                :disabled="!valid"
              >
                <v-icon left>mdi-content-save</v-icon>
                Update Work History
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this work history entry?
          <br><strong>{{ form.organization_name || 'This work history' }}</strong>
          <br><br>This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteWorkHistory" :loading="deleting">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "WorkHistoryEdit",
  data() {
    return {
      loading: true,
      valid: false,
      saving: false,
      deleting: false,
      loadingUsers: false,
      startDateMenu: false,
      endDateMenu: false,
      deleteDialog: false,
      users: [],
      workHistory: null,
      
      // Form data
      form: {
        user_id: null,
        organization_name: "",
        job_title: "",
        city: "",
        address: "",
        start_date: null,
        end_date: null,
        description: "",
        is_active: true,
      },
      
      // Validation rules
      rules: {
        required: value => !!value || "This field is required",
        maxLength: max => value => !value || value.length <= max || `Maximum ${max} characters allowed`,
        endDateAfterStart: value => {
          if (!value || !this.form.start_date) return true;
          return new Date(value) >= new Date(this.form.start_date) || "End date must be after start date";
        },
      },
    };
  },

  mounted() {
    this.getUsers();
    this.getWorkHistory();
  },

  methods: {
    async getWorkHistory() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        const response = await axios.get(`/work_histories/${id}`);
        
        this.workHistory = response.data.data;
        
        // Populate form
        this.form = {
          user_id: this.workHistory.user_id,
          organization_name: this.workHistory.organization_name || "",
          job_title: this.workHistory.job_title || "",
          city: this.workHistory.city || "",
          address: this.workHistory.address || "",
          start_date: this.workHistory.start_date ? this.workHistory.start_date.split('T')[0] : null,
          end_date: this.workHistory.end_date ? this.workHistory.end_date.split('T')[0] : null,
          description: this.workHistory.description || "",
          is_active: this.workHistory.is_active,
        };
      } catch (error) {
        console.error("Error fetching work history:", error);
        if (error.response && error.response.status === 404) {
          this.$toast.error("Work history not found");
          this.$router.push("/work_histories");
        } else {
          this.$toast.error("Failed to load work history");
        }
      } finally {
        this.loading = false;
      }
    },

    async getUsers() {
      this.loadingUsers = true;
      try {
        const response = await axios.get("/users", {
          params: { rowsPerPage: 1000 }
        });
        
        this.users = response.data.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`.trim() || user.email,
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.loadingUsers = false;
      }
    },

    async updateWorkHistory() {
      if (!this.$refs.form.validate()) {
        this.$toast.error("Please fix validation errors");
        return;
      }

      this.saving = true;
      try {
        const id = this.$route.params.id;
        
        // Prepare data
        const data = { ...this.form };
        
        // Remove empty strings
        Object.keys(data).forEach(key => {
          if (data[key] === "") {
            data[key] = null;
          }
        });

        await axios.put(`/work_histories/${id}`, data);
        
        this.$toast.success("Work history updated successfully");
        this.getWorkHistory(); // Refresh data
      } catch (error) {
        console.error("Error updating work history:", error);
        
        if (error.response && error.response.data && error.response.data.errors) {
          // Display validation errors
          const errors = error.response.data.errors;
          Object.keys(errors).forEach(key => {
            this.$toast.error(errors[key][0]);
          });
        } else {
          this.$toast.error("Failed to update work history");
        }
      } finally {
        this.saving = false;
      }
    },

    confirmDelete() {
      this.deleteDialog = true;
    },

    async deleteWorkHistory() {
      this.deleting = true;
      try {
        const id = this.$route.params.id;
        await axios.post(`/work_histories/delete/${id}`);
        
        this.$toast.success("Work history deleted successfully");
        this.$router.push("/work_histories");
      } catch (error) {
        console.error("Error deleting work history:", error);
        this.$toast.error("Failed to delete work history");
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
      }
    },

    goBack() {
      this.$router.push("/work_histories");
    },

    getUserName(user) {
      if (!user) return 'N/A';
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'N/A';
    },

    getImageUrl(imagePath) {
      return this.mediaUrl + '/' + imagePath;
    },

    formatDateTime(dateTime) {
      if (!dateTime) return 'N/A';
      return new Date(dateTime).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 20px !important;
}

.v-text-field--outlined >>> fieldset {
  border-radius: 25px;
}

.v-textarea >>> .v-input__control .v-input__slot {
  border-radius: 25px;
}
</style>